// WbWeighbridgesPage — /weighbridge/settings/weighbridges (admin only).
//
// Phase C2 Group 1. Lists every weighbridge in the tenant with its
// current calibration status badge.

import { useNavigate } from 'react-router-dom';
import { Gauge, Plus } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Badge } from '../../../components/ui/Badge/Badge';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import {
  deriveWbDisplayStatus,
  useWbWeighbridges,
  type WbCurrentCalibration,
} from '../hooks/useWbWeighbridges';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbWeighbridgesPage.module.css';

function statusBadge(status: 'online' | 'offline' | 'maintenance'): JSX.Element {
  if (status === 'online') return <Badge variant="success">Online</Badge>;
  if (status === 'offline') return <Badge variant="danger">Offline</Badge>;
  return <Badge variant="warning">Maintenance</Badge>;
}

/**
 * Computes the calibration-expiry badge using current_calibration.valid_until.
 *   null            → "—"
 *   < today         → red "Expired"
 *   ≤ 30 days       → amber "Expires in N days"
 *   > 30 days       → green "Valid"
 */
export function CalibrationExpiryCell({
  calibration,
}: {
  calibration: WbCurrentCalibration | null;
}): JSX.Element {
  if (!calibration) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const validUntil = new Date(calibration.valid_until + 'T00:00:00');
  const msPerDay = 24 * 60 * 60 * 1000;
  const days = Math.round((validUntil.getTime() - today.getTime()) / msPerDay);

  if (days < 0) {
    return <Badge variant="danger">Expired</Badge>;
  }
  if (days <= 30) {
    return <Badge variant="warning">Expires in {days} {days === 1 ? 'day' : 'days'}</Badge>;
  }
  return <Badge variant="success">Valid</Badge>;
}

export function WbWeighbridgesPage(): JSX.Element {
  const navigate = useNavigate();
  // Same 60s poll the Dashboard tile row uses, so the two surfaces don't
  // just agree once on load and then drift apart as agents connect/drop.
  const wbQuery = useWbWeighbridges({}, { enablePolling: true });
  const list = wbQuery.data?.weighbridges ?? [];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Weighbridges"
        subtitle="Physical scales and their hardware configuration"
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/weighbridge/settings/weighbridges/new')}
          >
            Add Weighbridge
          </Button>
        }
      />

      {wbQuery.isError ? (
        <p className={styles.error}>{extractErrorMessage(wbQuery.error)}</p>
      ) : null}

      <Card flush>
        {wbQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading weighbridges" />
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            icon={Gauge}
            title="No weighbridges yet"
            description="Add a weighbridge to start capturing weight tickets."
            action={
              <Button
                variant="primary"
                icon={Plus}
                onClick={() => navigate('/weighbridge/settings/weighbridges/new')}
              >
                Add Weighbridge
              </Button>
            }
          />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Yard</th>
                <th>Status</th>
                <th>Trade Approved</th>
                <th>Calibration</th>
              </tr>
            </thead>
            <tbody>
              {list.map((w) => (
                <tr
                  key={w.enc_id}
                  onClick={() =>
                    navigate(`/weighbridge/settings/weighbridges/${w.enc_id}`)
                  }
                >
                  <td>{w.name}</td>
                  <td className={styles.codeCell}>{w.code}</td>
                  <td>{w.yard.name}</td>
                  <td>{statusBadge(deriveWbDisplayStatus(w))}</td>
                  <td>{w.is_trade_approved ? 'Yes' : 'No'}</td>
                  <td>
                    <CalibrationExpiryCell calibration={w.current_calibration} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
